import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { captureSummary, canShareImage, downloadImage } from './shareSummary';
import './summary-share.css';

function ShareIcon() {
  return <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M8 6l4-4 4 4M12 2v13M7 10H4v11h16V10h-3" /></svg>;
}

export default function SummaryShareButton({ targetRef, title, subtitle, fileName }) {
  const [image, setImage] = useState(null);
  const [preparing, setPreparing] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [error, setError] = useState('');
  const trigger = useRef(null), dialog = useRef(null);
  const sharingRef = useRef(false);
  sharingRef.current = sharing;
  const headingId = useId();
  const supported = image && canShareImage(image.file);
  useEffect(() => {
    if (!image) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.current?.focus();
    const onKey = event => {
      if (event.key === 'Escape' && !sharingRef.current) setImage(null);
      if (event.key === 'Tab') {
        const buttons = dialog.current?.querySelectorAll('button:not(:disabled)');
        if (!buttons?.length) return;
        const first = buttons[0], last = buttons[buttons.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = previousOverflow; trigger.current?.focus({ preventScroll: true }); };
  }, [image]);
  const prepare = async () => {
    setPreparing(true); setError('');
    try { setImage(await captureSummary(targetRef.current, { subtitle, fileName })); }
    catch { setError('Görsel hazırlanamadı. Tekrar deneyin.'); }
    finally { setPreparing(false); }
  };
  const share = async () => {
    setSharing(true); setError('');
    // A separate tap keeps the native share call within the user's gesture,
    // even if rendering the JPEG took several seconds.
    try { await navigator.share({ files: [image.file], title }); }
    catch (failure) { if (failure.name !== 'AbortError') setError('Paylaşım açılamadı. JPEG indir seçeneğini kullanabilirsiniz.'); }
    finally { setSharing(false); }
  };
  return <div className="summary-share-control" data-export-exclude>
    <button ref={trigger} type="button" className="summary-share-button" aria-label={`${title} paylaş`} disabled={preparing} onClick={prepare}><ShareIcon />{preparing ? 'Hazırlanıyor…' : 'Paylaş'}</button>
    {error && !image && <small role="alert">{error}</small>}
    {image && createPortal(<div className="summary-share-backdrop" onClick={event => { if (event.target === event.currentTarget && !sharing) setImage(null); }}><section ref={dialog} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby={headingId} className="summary-share-dialog"><header><h2 id={headingId}>{title}</h2><button type="button" disabled={sharing} onClick={() => setImage(null)}>Kapat</button></header><div className="summary-share-preview"><img src={image.dataUrl} alt={`${title} paylaşım önizlemesi`} /></div>{error && <p role="alert">{error}</p>}{!supported && <p>JPEG’i indirip istediğiniz uygulamadan paylaşabilirsiniz.</p>}<footer>{supported && <button type="button" className="primary" disabled={sharing} onClick={share}><ShareIcon />{sharing ? 'Paylaşılıyor…' : 'Telefonda paylaş'}</button>}<button type="button" disabled={sharing} onClick={() => downloadImage(image.file)}>JPEG indir</button></footer></section></div>, document.body)}
  </div>;
}
